import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, ChevronLeft, Home as HomeIcon, Landmark, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import PublicLayout from "@/components/layout/PublicLayout";
import { useLang } from "@/context/LanguageContext";
import PropertyCard from "@/components/PropertyCard";
import { Button } from "@/components/ui/button";
import { setMeta, setJsonLd, removeJsonLd, setCanonical } from "@/lib/seo";

export default function CityPage() {
  const { citySlug } = useParams();
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api.get(`/public/city/${citySlug}`)
      .then((r) => {
        const d = r.data;
        setData(d);
        document.title = `Case vacanze e B&B a ${d.city} | Quattro Accommodations`;
        setMeta("description", `Scopri ${d.count} case vacanze e B&B a ${d.city}${d.region ? ", " + d.region : ""}. Prenota direttamente con il proprietario, senza intermediari e con le migliori condizioni.`);
        setCanonical(`${window.location.origin}/case-vacanze/${citySlug}`);
        setJsonLd("ld-city", {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": `Case vacanze e B&B a ${d.city}`,
          "description": `Case vacanze, B&B e appartamenti turistici a ${d.city}.`,
          "mainEntity": {
            "@type": "ItemList",
            "itemListElement": d.properties.map((p, i) => ({
              "@type": "ListItem", "position": i + 1,
              "url": `${window.location.origin}/proprieta/${p.slug}`, "name": p.name,
            })),
          },
        });
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    return () => removeJsonLd("ld-city");
  }, [citySlug]);

  if (loading) return <PublicLayout><div className="max-w-7xl mx-auto px-6 py-32 text-center text-brand-plum/50">Caricamento...</div></PublicLayout>;

  if (notFound || !data) return (
    <PublicLayout>
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="font-serif text-4xl text-brand-plum mb-3">Destinazione non trovata</h1>
        <p className="text-brand-plum/60 mb-6">Non abbiamo ancora case vacanze pubblicate in questa località.</p>
        <Link to="/case-vacanze"><Button className="rounded-full bg-brand-plum text-white">Vedi tutte le case vacanze e i B&B</Button></Link>
      </div>
    </PublicLayout>
  );

  return (
    <PublicLayout>
      <section className="bg-brand-plum text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
          <Link to="/case-vacanze" className="inline-flex items-center gap-1 text-sm text-white/60 hover:text-brand-teal mb-4"><ChevronLeft size={15} /> {t("city.back")}</Link>
          <p className="text-xs font-mono uppercase tracking-[0.22em] font-semibold text-brand-tealGlow mb-3 flex items-center gap-1.5"><MapPin size={13} /> {data.region || "Italia"}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif">{t("city.title", { city: data.city })}</h1>
          <p className="mt-4 text-white/75 max-w-2xl">
            {t("city.desc", { count: data.count, sel: data.count === 1 ? t("city.selectedOne") : t("city.selected"), city: data.city })}
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="prose max-w-none mb-8">
          <p className="text-brand-plum/70 leading-relaxed">
            {data.city} {t("city.intro")}
          </p>
        </div>

        {data.attractions?.length > 0 && (
          <div className="mb-10 flex flex-wrap gap-2" data-testid="city-attractions">
            <span className="text-sm text-brand-plum/50 flex items-center gap-1.5 mr-1"><Landmark size={15} className="text-brand-teal" /> {t("city.nearbyLabel")}</span>
            {data.attractions.map((a) => (
              <Link key={a.slug} to={`/case-vacanze-vicino-a/${a.slug}`} data-testid={`city-attraction-${a.slug}`} className="text-sm px-3.5 py-1.5 rounded-full border border-brand-plum/15 bg-white text-brand-plum/80 hover:border-brand-teal hover:text-brand-teal transition-colors flex items-center gap-1">
                {a.name} <ArrowRight size={13} />
              </Link>
            ))}
          </div>
        )}

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {data.properties.map((p) => <PropertyCard key={p.id} property={p} />)}
        </div>
        {data.properties.length === 0 && (
          <div className="text-center py-20 text-brand-plum/50">
            <HomeIcon size={40} className="mx-auto mb-3 text-brand-plum/30" />
            <p className="font-serif text-2xl text-brand-plum">{t("city.none")}</p>
          </div>
        )}
      </section>
    </PublicLayout>
  );
}
